import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, effect, ElementRef, viewChild, inject, untracked, OnDestroy } from '@angular/core';
import { extend, NgtArgs, injectStore } from 'angular-three';
import { ArService } from '../services/ar.service';
import { AudioService, ConfettiService } from '@kanek/shared-ui';
import { NgtsHTML, NgtsHTMLContent, NgtsHTMLOptions } from 'angular-three-soba/misc';
import { NgtsOrbitControls } from 'angular-three-soba/controls';
import * as THREE from 'three';
import {
  CylinderGeometry,
  CircleGeometry,
  MeshStandardMaterial,
  ExtrudeGeometry,
  Shape,
  AmbientLight,
  DirectionalLight,
  HemisphereLight
} from 'three';
import { WheelGameSettings, WHEEL_SIZE_PIXELS } from '@kanek/shared-data-access';
import { Wheel } from 'spin-wheel';
import { WheelConfigMapper } from '../plugins/wheel/wheel-config-mapper';
import { calculateHTMLDimensions3D } from '../utils/three-scene.utils';
import { EMPIRICAL_CORRECTIONS } from '../constants/three-scene.constants';

// Extend Three.js objects for Angular Three
extend({
  CylinderGeometry,
  CircleGeometry,
  MeshStandardMaterial,
  ExtrudeGeometry,
  AmbientLight,
  DirectionalLight,
  HemisphereLight
});

/**
 * Wheel Scene Component
 *
 * Declarative Angular Three component that renders the complete wheel game in 3D:
 * - Cylinder base (auto-sized from wheelSize)
 * - Arrow pointer (points down at wheel center)
 * - HTML wheel overlay (projected onto cylinder face)
 *
 * Architecture:
 * - Pure presentation component (stateless)
 * - All state passed via @Input()
 * - Reactive geometry using computed signals
 * - Follows Angular Three / React Three Fiber patterns
 *
 * Usage:
 * ```html
 * <ngt-canvas>
 *   <lib-wheel-scene
 *     [settings]="wheelSettings"
 *     [wheelProps]="wheelProps" />
 * </ngt-canvas>
 * ```
 */
@Component({
  selector: 'lib-wheel-scene',
  standalone: true,
  imports: [NgtsHTML, NgtsHTMLContent, NgtArgs, NgtsOrbitControls],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!-- OrbitControls (conditional on enableCameraControls from userData) -->
    @if (enableCameraControls()) {
      <ngts-orbit-controls
        [options]="{
          enableDamping: true,
          dampingFactor: 0.05,
          enablePan: true
        }" />
    }

    <!-- Three-Point Lighting System -->

    <!-- Ambient: Base illumination -->
    <ngt-ambient-light [intensity]="0.8" />

    <!-- Hemisphere: Natural sky/ground gradient lighting -->
    <ngt-hemisphere-light
      [skyColor]="16777215"
      [groundColor]="4473924"
      [intensity]="0.5"
    />

    <!-- Key Light: Main directional (strongest, casts shadows) -->
    <ngt-directional-light
      [position]="[5, 8, 5]"
      [intensity]="2.5"
      [castShadow]="true"
    />

    <!-- Fill Light: Softer secondary (reduces harsh shadows) -->
    <ngt-directional-light
      [position]="[-3, 4, -2]"
      [intensity]="0.8"
    />

    <!-- Rim Light: Back-highlight (adds depth and separation) -->
    <ngt-directional-light
      [position]="[0, 3, -6]"
      [intensity]="1.0"
    />

    <!-- Scene Group (rotated for AR mode) -->
    <ngt-group [rotation]="sceneGroupRotation()">
      <!-- Cylinder Base -->
      <ngt-mesh
        #cylinderMesh
        [rotation]="[Math.PI / 2, 0, 0]"
        [castShadow]="settingsData()?.castShadow ?? true">
        <ngt-value [rawValue]="cylinderGeometry()" attach="geometry" />
        <ngt-value [rawValue]="cylinderMaterial()" attach="material" />
      </ngt-mesh>

      <!-- Arrow Pointer -->
      <ngt-mesh
        #arrowMesh
        [position]="arrowPosition()"
        [rotation]="[0, 0, Math.PI]"
        [castShadow]="settingsData()?.castShadow ?? true">
        <ngt-value [rawValue]="arrowGeometry()" attach="geometry" />
        <ngt-value [rawValue]="arrowMaterial()" attach="material" />
      </ngt-mesh>

      <!-- Reflective Overlay Mesh (glass-like with shadows and reflections) -->
      <ngt-mesh
        [position]="htmlReflectiveOverlayPosition()"
        [receiveShadow]="true"
        [castShadow]="false">
        <ngt-circle-geometry *args="[htmlReflectiveOverlayRadius(), 64]" />
        <ngt-mesh-standard-material
          [transparent]="true"
          [opacity]="0.2"
          [roughness]="0.0"
          [metalness]="1.0"
          [side]="FrontSide"
          [depthWrite]="false"
          [blending]="Blending"]
        />
      </ngt-mesh>

      <!-- HTML Wheel Overlay -->
      <ngts-html [options]="htmlOverlayOptions()">
        <!-- Occlusion geometry: 0.5 units matches HTML wheel size in NgtsHTML coordinate space -->
        <ngt-circle-geometry
          data-occlusion-geometry
          *args="[0.5, 64]"
        />
        <!-- Custom occlusion material to avoid shader compilation errors -->
        <ngt-mesh-basic-material
          data-occlusion-material
          [colorWrite]="false"
        />
        <div [ngtsHTMLContent]="htmlContentOptions()">
          <div #wheelHost [style.width]="wheelDimensions()" [style.height]="wheelDimensions()"></div>
        </div>
      </ngts-html>
    </ngt-group>
  `,
  styles: [`
    :host {
      display: contents;
    }
  `]
})
export class WheelSceneComponent implements OnDestroy {
  // Inputs for direct usage (e.g., playground)
  settings = input<WheelGameSettings | null>(null);
  wheelContainer = input<HTMLElement | null>(null);

  // ViewChild to get the rendered wheel host element
  private wheelHost = viewChild<ElementRef<HTMLDivElement>>('wheelHost');

  // ViewChild to get OrbitControls instance (when enabled)
  private orbitControlsRef = viewChild(NgtsOrbitControls);
  
  // ViewChild references to meshes for direct material updates
  private cylinderMeshRef = viewChild<ElementRef<THREE.Mesh>>('cylinderMesh');
  private arrowMeshRef = viewChild<ElementRef<THREE.Mesh>>('arrowMesh');

  // Wheel instance (initialized when wheelHost is ready)
  private wheel: Wheel | null = null;

  // Track previous settings and items to detect changes requiring wheel recreation
  private previousSettings: string | null = null;
  private previousItems: string | null = null;

  private store = injectStore();
  private arService = inject(ArService);
  private audioService = inject(AudioService);
  private confettiService = inject(ConfettiService);

  /**
   * Extract only spin-wheel relevant settings (excludes 3D-only properties)
   * This determines what settings changes should trigger wheel HTML recreation
   */
  private getSpinWheelRelevantSettings(settings: WheelGameSettings | null): any {
    if (!settings) return null;
    
    const {
      // Exclude 3D-only settings (handled by material update effect)
      cylinderColor,
      cylinderOpacity,
      cylinderRoughness,
      cylinderMetalness,
      cylinderHeight,
      arrowColor,
      arrowWidth,
      arrowHeight,
      arrowDepth,
      castShadow,
      // Exclude internal flags
      _resetRequested,
      // Include everything else (visual, label, advanced settings)
      ...spinWheelSettings
    } = settings as any;
    
    return spinWheelSettings;
  }

  // Computed signals that read from scene.userData when available (sceneGraph usage)
  // or fall back to inputs (direct usage like playground)
  protected settingsData = computed(() => {
    const inputSettings = this.settings();
    
    // Read scene via untracked to avoid subscribing to 60fps NgtStore clock updates.
    // The configSignal itself is still called in tracked context so reactive config changes work.
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    const serviceSettings = config?.['settings'] as WheelGameSettings | undefined;
    
    return inputSettings || serviceSettings || null;
  });

  // Computed signal for camera controls flag from userData
  protected enableCameraControls = computed(() => {
    const scene = untracked(() => this.store.get()).scene;
    const fromUserData = scene?.userData?.['enableCameraControls'] ?? false;

    // Enable OrbitControls when AR bypass is active (preview mode)
    const bypassActive = this.arService.shouldBypassAR;
    return bypassActive || fromUserData;
  });

  // Computed signal for AR mode flag from userData
  protected enableAR = computed(() => {
    const scene = untracked(() => this.store.get()).scene;
    const fromUserData = scene?.userData?.['enableAR'] ?? false;

    // Disable AR rotation when bypass is active (render upright like preview)
    const bypassActive = this.arService.shouldBypassAR;
    return fromUserData && !bypassActive;
  });

  // Computed scene group rotation based on AR mode
  // In AR mode, rotate to make scene lie flat on marker
  protected sceneGroupRotation = computed(
    () => {
      const arMode = this.enableAR();
      return arMode ? [-Math.PI / 2, 0, 0] : [0, 0, 0];
    },
    { equal: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] }
  );

  // Computed wheel dimensions based on wheelSize setting
  protected wheelDimensions = computed(() => {
    const settings = this.settingsData();
    const wheelSize = settings?.wheelSize || 'medium';
    const pixels = WHEEL_SIZE_PIXELS[wheelSize as keyof typeof WHEEL_SIZE_PIXELS];
    return `${pixels}px`;
  });

  // Computed items data from scene.userData or input
  protected itemsData = computed(() => {
    // Read scene via untracked to avoid frame-reactive subscription.
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    const items = config?.['items'];
    return items || [];
  });

  // Computed: Base HTML dimensions converted to 3D units (calculated once, reused everywhere)
  // Uses shared utility for consistent conversion across all scene types.
  // store.get() is wrapped in untracked() so this computed does NOT re-run on every NgtStore
  // clock tick (which would recreate geometry/material objects at 60Hz → GPU leak).
  protected htmlDimensions3D = computed<{ width: number; height: number }>(() => {
    const settings = this.settingsData();
    const wheelSize = (settings?.wheelSize || 'medium') as 'small' | 'medium' | 'large';
    const wheelPixels = WHEEL_SIZE_PIXELS[wheelSize];

    return calculateHTMLDimensions3D(wheelPixels, wheelPixels, untracked(() => this.store.get()));
  });

  constructor() {
    // Store OrbitControls reference in scene.userData when ready
    effect(() => {
      const controls = this.orbitControlsRef();
      const scene = untracked(() => this.store.get()).scene;
      if (controls && scene) {
        const orbitControls = controls.controls();
        scene.userData['orbitControls'] = orbitControls;
      }
    });
    
    // Update material properties directly when settings change (standard Three.js approach)
    effect(() => {
      const settings = this.settingsData();
      const cylinderMesh = this.cylinderMeshRef();
      const arrowMesh = this.arrowMeshRef();
      
      if (!settings) return;
      
      // Update cylinder material
      if (cylinderMesh?.nativeElement) {
        const material = cylinderMesh.nativeElement.material as THREE.MeshStandardMaterial;
        if (material) {
          if (settings.cylinderColor) material.color.set(settings.cylinderColor);
          if (settings.cylinderOpacity !== undefined) material.opacity = settings.cylinderOpacity;
          if (settings.cylinderRoughness !== undefined) material.roughness = settings.cylinderRoughness;
          if (settings.cylinderMetalness !== undefined) material.metalness = settings.cylinderMetalness;
          material.needsUpdate = true;
        }
      }
      
      // Update arrow material
      if (arrowMesh?.nativeElement) {
        const material = arrowMesh.nativeElement.material as THREE.MeshStandardMaterial;
        if (material && settings.arrowColor) {
          material.color.set(settings.arrowColor);
          material.needsUpdate = true;
        }
      }
    });

    // Initialize wheel when wheelHost is ready (for GameEngine sceneGraph usage)
    effect(() => {
      const host = this.wheelHost();
      const settings = this.settingsData();
      const items = this.itemsData();

      // Check if reset was requested
      const resetRequested = (settings as any)?._resetRequested;

      // Initialize or reinitialize if we have all required data
      if (host?.nativeElement && settings && items && items.length > 0) {
        // If reset was requested and wheel exists, just reset (don't destroy)
        if (this.wheel && resetRequested) {
          this.resetGame();
          
          // Clear the reset flag to prevent re-triggering on next config change
          const scene = untracked(() => this.store.get()).scene;
          const configSignal = scene?.userData?.['configSignal'];
          if (configSignal) {
            const currentConfig = configSignal();
            const newSettings = { ...currentConfig.settings };
            delete (newSettings as any)._resetRequested;
            configSignal.set({
              ...currentConfig,
              settings: newSettings
            });
          }
          
          return;
        }
        
        // Check if settings or items changed (requires wheel recreation)
        // Only compare spin-wheel relevant settings (exclude 3D-only properties)
        const spinWheelSettings = this.getSpinWheelRelevantSettings(settings);
        const currentSettingsHash = JSON.stringify(spinWheelSettings);
        const currentItemsHash = JSON.stringify(items);
        const settingsChanged = this.previousSettings !== currentSettingsHash;
        const itemsChanged = this.previousItems !== currentItemsHash;
        
        if (this.wheel && (settingsChanged || itemsChanged)) {
          // Destroy existing wheel
          while (host.nativeElement.firstChild) {
            host.nativeElement.removeChild(host.nativeElement.firstChild);
          }
          this.wheel = null;
        } else if (this.wheel) {
          return;
        }

        // Store current settings and items for next comparison
        this.previousSettings = currentSettingsHash;
        this.previousItems = currentItemsHash;

        // Initialize wheel
        this.initializeWheel(host.nativeElement, settings, items);
      }
    });

    // Support for playground mode: append pre-initialized container if provided
    effect(() => {
      const host = this.wheelHost();
      const container = this.wheelContainer(); // From input

      if (host && container && host.nativeElement) {
        host.nativeElement.appendChild(container);
      }
    });
  }

  /**
   * Initialize wheel directly in the wheelHost div
   * Used when WheelScene is loaded via GameEngine sceneGraph
   */
  private async initializeWheel(
    container: HTMLElement,
    settings: WheelGameSettings,
    items: Array<{ label?: string; labelColor?: string; backgroundColor?: string }>
  ): Promise<void> {
    try {
      // Explicitly set container size based on wheelSize setting
      const wheelSize = settings.wheelSize || 'medium';
      const pixels = WHEEL_SIZE_PIXELS[wheelSize as keyof typeof WHEEL_SIZE_PIXELS];
      const size = `${pixels}px`;

      container.style.width = size;
      container.style.height = size;

      // Preload tick sound before creating wheel
      await this.audioService.preload('assets/sfx/tick.mp3');

      const wheelProps = await WheelConfigMapper.toWheelPropsAsync(settings, items);

      // Add tick sound callback to wheelProps (required by spin-wheel library)
      const wheelPropsWithCallbacks = {
        ...wheelProps,
        onCurrentIndexChange: (event: any) => {
          this.audioService.play('assets/sfx/tick.mp3', { volume: 1.0 });
        }
      };

      this.wheel = new Wheel(container, wheelPropsWithCallbacks);

      // Store wheel instance, reset method, and services in scene.userData
      const scene = untracked(() => this.store.get()).scene;
      if (scene) {
        scene.userData['wheelInstance'] = this.wheel;
        scene.userData['resetGame'] = () => this.resetGame();
        scene.userData['confettiService'] = this.confettiService;
        scene.userData['audioService'] = this.audioService;
      }
    } catch (error) {
      console.error('[WheelScene] Failed to initialize wheel:', error);
    }
  }

  protected readonly Math = Math;
  protected readonly FrontSide = THREE.FrontSide;
  protected readonly Blending = THREE.AdditiveBlending;

  // ============================================================================
  // Cylinder Geometry (Auto-sized from wheelSize)
  // ============================================================================

  /**
   * Calculate cylinder radius from wheel size preset
   * Uses cached HTML dimensions and adds 5% border margin
   */
  protected cylinderRadius = computed(() => {
    const dimensions = this.htmlDimensions3D();
    // Wheel is circular: radius = width / 2, then add 5% border
    const wheelRadius = dimensions.width / 2;
    return wheelRadius * EMPIRICAL_CORRECTIONS.WHEEL_BORDER_MARGIN;
  });

  protected cylinderHeight = computed(() => {
    return 0.5; // Fixed height for cylinder base
  });

  /**
   * Wheel radius without border - for HTML occlusion geometry sizing
   */
  protected wheelRadius = computed(() => {
    const dimensions = this.htmlDimensions3D();
    // Wheel radius without 5% border
    return dimensions.width / 2;
  });

  /**
   * Reflective overlay position (slightly in front of HTML)
   */
  protected htmlReflectiveOverlayPosition = computed<[number, number, number]>(() => {
    const settings = this.settingsData();
    const defaultCylinderHeight = 0.25;
    const cylinderHeight = settings?.cylinderHeight ?? defaultCylinderHeight;
    const htmlZOffset = cylinderHeight / 2 + 0.01;
    // Position overlay 0.01 units in front of HTML
    return [0, 0, htmlZOffset + 0.01];
  });

  /**
   * Reflective overlay radius (matches HTML wheel exactly)
   */
  protected htmlReflectiveOverlayRadius = computed(() => {
    // Use corrected HTML dimensions directly (1% correction already applied in htmlDimensions3D)
    return this.wheelRadius();
  });

  protected cylinderGeometry = computed(() => {
    const settings = this.settingsData();
    // Fallback defaults for all properties (especially for old campaigns)
    const defaultRadius = 9.975; // Medium wheel + 5% border
    const defaultHeight = 0.25;

    if (!settings) {
      return new CylinderGeometry(defaultRadius, defaultRadius, defaultHeight, 64, 1, false);
    }

    const radius = this.cylinderRadius();
    const height = settings.cylinderHeight ?? defaultHeight;

    const geometry = new CylinderGeometry(
      radius,
      radius,
      height,
      64,  // segments for smooth appearance
      1,
      false
    );
    return geometry;
  });

  protected cylinderMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.cylinderColor ?? '#ed1f24',
      transparent: true,
      opacity: settings?.cylinderOpacity ?? 0.8,
      roughness: settings?.cylinderRoughness ?? 0.5,
      metalness: settings?.cylinderMetalness ?? 0.0
    });
  });

  // ============================================================================
  // Arrow Geometry (Triangle pointing down)
  // ============================================================================

  protected arrowGeometry = computed(() => {
    const settings = this.settingsData();

    // Fallback defaults for arrow (especially for old campaigns)
    const defaultWidth = 0.64;
    const defaultHeight = 0.32;
    const defaultDepth = 0.05;

    if (!settings) {
      return new ExtrudeGeometry(new Shape(), { depth: defaultDepth, bevelEnabled: false });
    }

    const width = settings.arrowWidth ?? defaultWidth;
    const height = settings.arrowHeight ?? defaultHeight;
    const depth = settings.arrowDepth ?? defaultDepth;

    // Create triangle shape (pointing down)
    const shape = new Shape();
    shape.moveTo(0, 0);                  // Top left
    shape.lineTo(width / 2, height);     // Bottom center (point)
    shape.lineTo(width, 0);              // Top right
    shape.closePath();

    // Extrude to give it depth
    const geometry = new ExtrudeGeometry(shape, {
      depth: depth,
      bevelEnabled: false
    });

    // Center the geometry
    geometry.translate(-width / 2, -height / 2, -depth / 2);
    return geometry;
  });

  protected arrowMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.arrowColor ?? '#222',
      roughness: 0.5,
      metalness: 0.5
    });
  });

  /**
   * Arrow position: Above the wheel at top edge of cylinder
   * Since cylinder is rotated 90° on X-axis, arrow needs:
   * - Y position: at top of wheel (radius + borderWidth/2)
   * - Z position: in front of HTML (cylinderHeight/2 + offset)
   */
  protected arrowPosition = computed(() => {
    const settings = this.settingsData();

    // Fallback defaults (especially for old campaigns)
    const defaultCylinderHeight = 0.25;
    const defaultPosition: [number, number, number] = [0, 9.975, 0.65];
    const defaultHeight = 0.32;
    const defaultDepth = 0.05;

    if (!settings) {
      return defaultPosition;
    }

    const depth = settings.arrowDepth ?? defaultDepth;
    const height = settings.arrowHeight ?? defaultHeight;

    const cylinderRadius = this.cylinderRadius();
    const wheelRadius = cylinderRadius / 1.05; // Remove border to get wheel radius
    const borderWidth = wheelRadius * 0.05;
    const cylinderHeight = settings.cylinderHeight ?? defaultCylinderHeight;
    const arrowYOffset = wheelRadius + (borderWidth / 2) - (height / 2);
    const arrowZOffset = (cylinderHeight / 2) + (depth / 2) + 0.01; // In front of HTML

    return [0, arrowYOffset, arrowZOffset] as [number, number, number];
  });

  // ============================================================================
  // HTML Overlay (Wheel face projected onto cylinder)
  // ============================================================================

  protected htmlOverlayOptions = computed((): Partial<NgtsHTMLOptions> => {
    const settings = this.settingsData();

    // Fallback defaults (especially for old campaigns)
    const defaultCylinderHeight = 0.25;

    const cylinderHeight = settings?.cylinderHeight ?? defaultCylinderHeight;

    // Z offset: Just slightly in front of cylinder face
    // Cylinder is rotated 90° on X, so HTML positioned along Z-axis
    const htmlZOffset = cylinderHeight / 2 + 0.01; // add 0.01 to place HTML just slightly in front of cylinder face

    return {
      occlude: 'blending',
      transform: true,
      scale: 1.0, // Always 1.0 to maintain visual alignment with 3D geometry
      position: [0, 0, htmlZOffset] as [number, number, number],
      rotation: [0, 0, 0] as [number, number, number]  // No rotation needed
    };
  });

  protected htmlContentOptions = computed(() => {
    return {
      distanceFactor: 0,  // HTML at pixel size, 3D uses unit-based dimensions
      zIndexRange: [100, 1] as [number, number]  // CTA button at 101 ensures it appears on top
    };
  });

  /**
   * Get OrbitControls instance (for ExperiencePreview to manipulate)
   * Returns null if controls are not enabled
   */
  public getOrbitControls(): any {
    return this.orbitControlsRef()?.controls() ?? null;
  }

  ngOnDestroy(): void {
    // Dispose Three.js GPU resources to prevent memory leaks
    this.cylinderGeometry()?.dispose();
    this.arrowGeometry()?.dispose();
    (this.cylinderMaterial() as MeshStandardMaterial | null)?.dispose();
    (this.arrowMaterial() as MeshStandardMaterial | null)?.dispose();
  }

  /**
   * Reset the wheel game state
   * Called by refresh button in Experience Studio
   * 
   * For wheel game, we just need to re-enable the CTA button.
   * The button is managed by GameEngine's showCTA signal.
   */
  resetGame(): void {
    // Reset the wheel library's internal state
    if (this.wheel) {
      this.wheel.stop();
    }
    
    // Re-enable the CTA button via GameEngine's showCTA signal
    const scene = untracked(() => this.store.get()).scene;
    const showCTA = scene?.userData?.['showCTA'];
    
    if (showCTA) {
      showCTA.set(true);
    }
  }
}
